import React, { useEffect, useState } from "react";
import {
  FaUserCircle, FaBuilding, FaCalendarAlt,
  FaHistory, FaDownload, FaBell, FaChevronDown
} from "react-icons/fa";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import TenantSidebar from "../components/Sidebar/TenantSidebar";
import "./TenantDashboard.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const TenantDashboard = () => {
  // --- Multiple Stays Engine Hooks ---
  const [stays, setStays] = useState([]);
  const [selectedStayIndex, setSelectedStayIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [totalDays, setTotalDays] = useState(0);
  const [passedDays, setPassedDays] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Safely grab the active stay object from the array
  const tenant = stays[selectedStayIndex] || null;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const parsePureDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  const displayDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // Fetch contextual email stay logs
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      const user = storedUser ? JSON.parse(storedUser) : null;

      const emailToUse = user?.email_address || user?.email;

      if (!emailToUse) {
        setError("No user email found in current session context.");
        setLoading(false);
        return;
      }

      fetch(`https://ikaze-thouse-backend.vercel.app/api/v1/tenants/tenantByEmail/${encodeURIComponent(emailToUse.trim())}`)
        .then((res) => res.json())
        .then((result) => {
          if (!result.success) {
            setError(result.message || "Failed to load stay indices.");
            return;
          }

          const dataArray = Array.isArray(result.data) ? result.data : [result.data];

          if (dataArray.length === 0) {
            setError("No stay records found for this email.");
            return;
          }

          setStays(dataArray);
          setSelectedStayIndex(0);
        })
        .catch((err) => {
          console.error(err);
          setError("Error fetching server records.");
        })
        .finally(() => setLoading(false));
    } catch (e) {
      setError("Failed parsing internal session layout.");
      setLoading(false);
    }
  }, []);

  // Structural pipeline tracking date properties metrics calculation engine
  useEffect(() => {
    if (!tenant) return;

    if (tenant.arrival_date && tenant.departure_date) {
      const arrival = parsePureDate(tenant.arrival_date);
      const departure = parsePureDate(tenant.departure_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const total = Math.max(Math.round((departure - arrival) / 86400000), 0);
      const passed = Math.max(Math.round((today - arrival) / 86400000), 0);

      setTotalDays(total);
      setPassedDays(passed);
      setDaysRemaining(Math.max(total - passed, 0));
      setProgressPercent(total ? Math.min((passed / total) * 100, 100) : 0);
    } else {
      setTotalDays(0);
      setPassedDays(0);
      setDaysRemaining(0);
      setProgressPercent(0);
    }
  }, [selectedStayIndex, stays, tenant]);

  // ================= RECEIPT GENERATOR =================
  const handleGenerateReceipt = async () => {
    if (!tenant) return;

    const doc = new jsPDF();
    const primaryBlue = [0, 82, 179];
    const lightBlue = [52, 152, 219];
    const darkText = [31, 41, 55];

    const receiptId = `REC-${Math.floor(100000 + Math.random() * 900000)}`;
    const signatureImg = "/Images/signature.png";

    // Header branding elements
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(21, 18, 14, 14, 2, 2, "F");
    doc.setFillColor(...lightBlue);
    doc.roundedRect(22, 19, 12, 12, 1, 1, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("TH", 28, 26.5, { align: "center" });

    doc.setFontSize(18);
    doc.setTextColor(...primaryBlue);
    doc.text("LaSplendide Apartments", 42, 27);

    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text("Premium Property Management", 42, 33);

    // Meta metadata cards right column
    doc.setFillColor(245, 247, 250);
    doc.roundedRect(140, 15, 55, 30, 3, 3, "F");
    doc.setTextColor(71, 85, 105);
    doc.text("RECEIPT NO:", 145, 25);
    doc.setTextColor(...darkText);
    doc.setFont("helvetica", "bold");
    doc.text(receiptId, 145, 31);

    doc.setFont("helvetica", "normal");
    doc.text("DATE:", 145, 38);
    doc.text(new Date().toLocaleDateString(), 158, 38);

    // ================= TENANT DETAILS BLOCK =================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...primaryBlue);
    doc.text("BILLED TO / TENANT:", 21, 54);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...darkText);
    doc.setFontSize(11);
    doc.text(tenant.guest_name ? tenant.guest_name.toUpperCase() : "-", 21, 61);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Email: ${tenant.email_address || tenant.email || "-"}`, 21, 67);
    doc.text(`Unit Reference: Unit ${tenant.appartment_unite || "-"}`, 21, 73);

    // Itemized table grid layout configurations
    const tableTop = 85;
    doc.setFillColor(...primaryBlue);
    doc.rect(15, tableTop, 180, 12, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);

    const colX = {
      desc: 18,
      checkIn: 62,
      checkOut: 94,
      usd: 126,
      rate: 150,
      rwf: 172
    };

    doc.text("DESCRIPTION", colX.desc, tableTop + 7);
    doc.text("CHECK IN", colX.checkIn, tableTop + 7);
    doc.text("CHECK OUT", colX.checkOut, tableTop + 7);
    doc.text("USD", colX.usd, tableTop + 7);
    doc.text("EXC. RATE", colX.rate, tableTop + 7);
    doc.text("RWF", colX.rwf, tableTop + 7);

    doc.setTextColor(...darkText);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const exchangeRateStr = tenant.exchange_rate ? `${Number(tenant.exchange_rate).toLocaleString()}` : "-";
    const depositExchangeRateStr = tenant.deposit_exchange_rate ? `${Number(tenant.deposit_exchange_rate).toLocaleString()}` : (tenant.exchange_rate ? `${Number(tenant.exchange_rate).toLocaleString()}` : "-");

    // Rent row
    doc.text("Monthly Apartment Rent", colX.desc, tableTop + 20);
    doc.text(displayDate(tenant.arrival_date), colX.checkIn, tableTop + 20);
    doc.text(displayDate(tenant.departure_date), colX.checkOut, tableTop + 20);
    doc.text(`$${Number(tenant.rent_usd || 0).toLocaleString()}`, colX.usd, tableTop + 20);
    doc.text(exchangeRateStr, colX.rate, tableTop + 20);
    doc.text(`${Number(tenant.rent_rwf || 0).toLocaleString()} RWF`, colX.rwf, tableTop + 20);

    // Resolved Deposit Values matching user requested separate breakdown structure
    const depositUsdVal = Number(tenant.deposites_usd || (tenant.deposit_currency === 'USD' ? tenant.deposites : 0) || (tenant.deposites && !tenant.deposit_currency ? tenant.deposites : 0) || 0);
    const depositRwfVal = Number(tenant.deposites_rwf || (tenant.deposit_currency === 'RWF' ? tenant.deposites : 0) || 0);

    doc.text("Security Deposit", colX.desc, tableTop + 32);
    doc.text("-", colX.checkIn, tableTop + 32);
    doc.text("-", colX.checkOut, tableTop + 32);
    doc.text(depositUsdVal > 0 ? `$${depositUsdVal.toLocaleString()}` : "-", colX.usd, tableTop + 32);
    doc.text(depositUsdVal > 0 ? depositExchangeRateStr : "-", colX.rate, tableTop + 32);
    doc.text(depositRwfVal > 0 ? `${depositRwfVal.toLocaleString()} RWF` : (depositUsdVal > 0 && tenant.exchange_rate ? `${(depositUsdVal * Number(tenant.exchange_rate)).toLocaleString()} RWF` : "-"), colX.rwf, tableTop + 32);

    doc.setDrawColor(220, 220, 220);
    doc.line(15, tableTop + 38, 195, tableTop + 38);

    const totalUSD = Number(tenant.rent_usd || 0) + depositUsdVal;
    const computedDepositRwf = depositRwfVal > 0 ? depositRwfVal : (depositUsdVal > 0 && tenant.exchange_rate ? depositUsdVal * Number(tenant.exchange_rate) : 0);
    const totalRWF = Number(tenant.rent_rwf || 0) + computedDepositRwf;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("TOTAL PAID:", colX.checkOut, tableTop + 52);

    doc.setTextColor(...primaryBlue);
    doc.text(totalUSD > 0 ? `$${totalUSD.toLocaleString()}` : "-", colX.usd, tableTop + 52);
    doc.text(totalRWF > 0 ? `${totalRWF.toLocaleString()} RWF` : "-", colX.rwf, tableTop + 52);

    const footerTop = 210;
    try {
      const imgData = await fetch(signatureImg)
        .then(r => r.blob())
        .then(blob => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }));
      doc.addImage(imgData, "PNG", 140, footerTop - 5, 45, 18);
    } catch (e) {
      console.log("Signature asset omitted or failed loading", e);
    }

    doc.setTextColor(...darkText);
    doc.setFontSize(10);
    doc.text("Authorized Signature:", 140, footerTop + 18);
    doc.text("Sifa KARIRE", 140, footerTop + 25);

    const productionUrl = "https://ikaze-transite-front-end-upiy.vercel.app";
    const qrUrl = `${productionUrl}/verify?email=${encodeURIComponent(tenant.email_address || tenant.email)}`;

    try {
      const qrCode = await QRCode.toDataURL(qrUrl);
      doc.addImage(qrCode, "PNG", 20, footerTop, 35, 35);
      doc.text("VALID RECEIPT SCAN", 22, footerTop + 38);
    } catch (err) {
      console.error("Failed to append QR structure", err);
    }

    const docName = tenant.guest_name ? tenant.guest_name.replace(/\s+/g, "_") : "Guest";
    doc.save(`GKM_Receipt_${docName}_Unit_${tenant.appartment_unite}.pdf`);
  };

  // ================= VISUAL PIE DISPLAY =================
  const pieData = {
    labels: ["Days Spent", "Days Left"],
    datasets: [{
      data: [passedDays, daysRemaining],
      backgroundColor: ["#4f46e5", "#e2e8f0"],
      borderWidth: 0
    }]
  };

  if (loading) return <div className="loader">Loading your stay history...</div>;
  if (error) return <div className="error-msg">{error}</div>;
  if (!tenant) return <div className="error-msg">No tenant details available.</div>;

  const firstName = tenant.guest_name ? tenant.guest_name.split(' ')[0] : "Guest";

  // Separate metrics computation for screen display block alignment
  const displayDepositUsd = Number(tenant.deposites_usd || (tenant.deposit_currency === 'USD' ? tenant.deposites : 0) || (tenant.deposites && !tenant.deposit_currency ? tenant.deposites : 0) || 0);
  const displayDepositExRate = Number(tenant.deposit_exchange_rate || tenant.exchange_rate || 0);
  const displayDepositRwf = Number(tenant.deposites_rwf || (tenant.deposit_currency === 'RWF' ? tenant.deposites : 0) || (displayDepositUsd > 0 && displayDepositExRate > 0 ? displayDepositUsd * displayDepositExRate : 0));

  return (
    <div className="dashboard-wrapper">
      <TenantSidebar showSidebar={showSidebar} setShowSidebar={setShowSidebar} isMobile={isMobile} />

      <main className="dashboard-main">
        <header className="dash-header">
          <div className="welcome-text">
            <h1>Welcome back, {firstName}!</h1>
            <p className="subtitle">Select your active or past stay below to audit lease structures.</p>

            {/* --- MULTIPLE STAYS DROPDOWN SELECTOR --- */}
            <div className="stay-selector-card">
              <div className="select-inner-wrapper">
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  maxWidth: '400px',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  padding: '0 12px',
                  boxSizing: 'border-box'
                }}>
                  <FaBuilding style={{
                    color: '#94a3b8',
                    marginRight: '10px',
                    flexShrink: 0,
                    fontSize: '16px'
                  }} />

                  <select                  
                    id="stay-select"
                    value={selectedStayIndex}
                    onChange={(e) => setSelectedStayIndex(Number(e.target.value))}
                    style={{
                      flex: 1,
                      width: '100%',
                      backgroundColor: 'transparent',
                      color: 'black',
                      border: 'none',
                      outline: 'none',
                      padding: '12px 24px 12px 0',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none'
                    }}
                  >
                    {stays.map((stay, index) => (
                      <option
                        key={stay.tenant_id || stay._id || index}
                        value={index}
                        style={{
                          backgroundColor: '#1e1b4b',
                          color: '#ffffff',
                          padding: '10px'
                        }}
                      >
                        Stay #{index + 1}: Unit {stay.appartment_unite || "N/A"} ({displayDate(stay.arrival_date)} - {displayDate(stay.departure_date)})
                      </option>
                    ))}
                  </select>

                  <FaChevronDown style={{
                    color: '#94a3b8',
                    position: 'absolute',
                    right: '12px',
                    pointerEvents: 'none',
                    fontSize: '12px'
                  }} />
                </div>
              </div>
            </div>
          </div>

          <div className="header-actions">
            <button className="icon-btn"><FaBell /></button>
            <div className="user-pill">
              <FaUserCircle /> <span>{tenant.guest_name || "User profile"}</span>
            </div>
          </div>
        </header>

        <section className="stats-grid">
          <div className="stat-card">
            <FaBuilding /> <span>Unit {tenant.appartment_unite || "-"}</span>
          </div>
          <div className="stat-card">
            <FaCalendarAlt /> <span>{displayDate(tenant.departure_date)}</span>
          </div>
          <div className="stat-card">
            <FaHistory /> <span>{daysRemaining} Days Left</span>
          </div>
        </section>

        <section className="content-layout">
          <div className="card chart-card">
            <h3>Stay Progress</h3>
            <div className="chart-container">
              <Pie key={selectedStayIndex} data={pieData} />
            </div>
          </div>

          <div className="card details-card">
            <h3>Lease Summary</h3>

            <div className="details-list">
              <div>Tenant Name: <strong>{tenant.guest_name || "-"}</strong></div>
              <div>Arrival: <strong>{displayDate(tenant.arrival_date)}</strong></div>
              <div>Rent USD: <strong>${Number(tenant.rent_usd || 0).toLocaleString()}</strong></div>
              <div>Exchange Rate: <strong>{tenant.exchange_rate ? `${Number(tenant.exchange_rate).toLocaleString()} RWF/$` : "-"}</strong></div>
              <div>Rent RWF: <strong>{Number(tenant.rent_rwf || 0).toLocaleString()} RWF</strong></div>
              
              <div style={{ marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>
                Deposite USD: <strong>{displayDepositUsd > 0 ? `$${displayDepositUsd.toLocaleString()}` : "$0"}</strong>
              </div>
              <div>Exchange Rate: <strong>{displayDepositExRate > 0 ? `${displayDepositExRate.toLocaleString()} RWF/$` : "-"}</strong></div>
              <div>Deposite RWF: <strong>{displayDepositRwf > 0 ? `${displayDepositRwf.toLocaleString()} RWF` : "0 RWF"}</strong></div>
            </div>

            <button className="primary-btn" onClick={handleGenerateReceipt}>
              <FaDownload /> Download Receipt
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default TenantDashboard;